import {
  Module,
  VuexModule,
  Action,
  Mutation,
  config
} from "vuex-module-decorators";

import { documentStore, configStore, globalStore } from "@/store/store";
import { DEFAULT_MINIMUM_BREAKPOINT_WIDTH } from "@/lib/breakpoints/breakpoints";
import { SelectorCSSName } from "@/types/selectors";
import { ElementStudioClipboard } from "@/types/copy-paste";
import defensiveCopy from "@/util/defensive-copy";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { Breakpoint } from "@/types/breakpoints";
import { findNode } from "@/lib/tree/tree";
import { Control } from "@/types/controls";
import { BreakdanceNotification } from "@/types/notifications";
import { TreeNode } from "@/types/tree";
import { StructurePanelNodesExpandedState } from "@/lib/structure-panel/expand-collapse";
import {
  CanvasDragPlaceholderPosition,
  CanvasPreviewWidth,
  InterfaceState,
  PanelState,
  UIPanel,
  UnrecoverableErrorMessage,
  CanvasState,
  ElementsThatAreUpdatingMap,
  ClosingConfirmations,
  ClosingConfirmationDefinition,
  ClosingConfirmationChosenOption,
  ClosingConfirmationItem,
  LastModifiedElementProperty,
  UISettings,
  ClosingConfirmationSubject,
  UIPanelWhichLoadFreshDataOnceOpened,
  KeyboardTriggeredActions
} from "@/types/ui";
import { RightClickMenuState } from "@/types/right-click";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import Vue from "vue";
import { scrollToActiveElement } from "@/lib/canvas/scroll-to-active-element";
import VueSimpleDeepset from "@/util/vue-simple-deepset";
import { getUiSettingFromLocalStorageOrDefaults } from "@/lib/ui-preference/ui-preference";
import { DocumentRevisionsData } from "@/types/revisions";
import { removeBannedPanelsFromPanelState } from "@/lib/breakdance-oxygen/panels";
import lodash from "lodash";
import { OxyNestedSelector } from "@/oxygen/classes/lib/types";
import { InlineEditingContext } from "@/types/inline-editor";
import { breakdanceUndo } from "@/lib/undo/undo";
import { getValuesFromComponent } from "@/lib/components/controls";

config.rawError = true;
@Module({ name: "ui", namespaced: true })
export default class UIModule extends VuexModule {
  mode: null | "builder" | "browse" = null;
  canvasDragPlaceholderPosition: null | CanvasDragPlaceholderPosition = null;
  activeSelectorName: null | SelectorCSSName = ""; // ".blue-section";
  activePresetId: null | string = null;
  activeElementId: null | TreeNode["id"] = null;
  builderBehaviorElementId: null | TreeNode["id"] = null;
  elementWithContentEditable: null | TreeNode["id"] = null;
  lastHoveredOnCanvasElementId: null | TreeNode["id"] = null;
  currentBreakpoint: Breakpoint = {
    id: BASE_BREAKPOINT_ID,
    label: "",
    minWidth: undefined,
    maxWidth: undefined,
    defaultPreviewWidth: DEFAULT_MINIMUM_BREAKPOINT_WIDTH
  };
  canvasPreviewWidth: CanvasPreviewWidth = {
    width: "100%",
    availableCanvasSize: {
      width: 0,
      height: 0
    }
  };
  currentlyDraggingCanvasElementId: null | TreeNode["id"] = null;
  // Element being added from the 'Add' panel
  currentlyDraggingAddElementSlug: null | string = null;
  isDragMixinDragging = false;
  isPreviewMode = false;
  interfaceState: InterfaceState = "loading";
  canvasState: CanvasState = "loading";
  unrecoverableErrorMessages: UnrecoverableErrorMessage[] = [];
  unsavedChangesPresent = false;
  elementStudioClipboard: ElementStudioClipboard = {
    copiedControl: false
  };
  consoleErrorsPresent = false;

  onCanvasRightClickMenuState: RightClickMenuState = "closed";
  onStructurePanelRightClickMenuState: RightClickMenuState = "closed";

  panelState: PanelState = {
    leftSidebarPanel: "closed",
    rightSidebarPanel: "closed",
    preferencesModalOpen: false,
    keyboardShortcutsHelpModalOpen: false,
    designLibraryModalOpen: false,
    elementStudioOpen: false,
    accordionState: {
      elementProperties: {},
      selectors: {},
      presets: {},
      globalSettings: {},
      dynamicDataFieldAttributes: {}
    },
    elementPropertiesPanel: {},
    shouldLoadFreshDataIntoStoreOnceOpened: {
      globalSettings: true,
      selectors: true,
      variables: true,
      presets: true,
      ai: true
    },
    globalSettingsPanelActiveViewMode: "controls",
    selectorsPanelPanelActiveViewMode: "controls",
    variablesPanelPanelActiveViewMode: "controls",
    presetsPanelActiveViewMode: "controls",
    oxyPanelIsMasked: false
  };

  keyboardTriggeredActions: KeyboardTriggeredActions = {
    focusCodeMirror: null,
    openClassesDropdown: null
  };

  closingConfirmations: ClosingConfirmations = {};

  readOnly = false;

  notifications: BreakdanceNotification[] = [];

  isLoggedOut = false;

  elementsThatAreUpdating: ElementsThatAreUpdatingMap = {};

  // todo - get this out of the UI store
  idsOfPostsToRegenerateCache: null | TreeNode["id"][] = null;

  lastModifiedElementProperty: LastModifiedElementProperty = {
    elementId: null,
    path: null
  };

  refreshSsrElementId: TreeNode["id"] | null = null;

  inlineEditingContext: InlineEditingContext | null = null;

  settings: UISettings = getUiSettingFromLocalStorageOrDefaults();

  /**
   * Nodes, that are:
   * – not present at this object,
   * – mapped to "undefined" value,
   * – mapped to "true" value,
   * are treated as expanded.
   */
  structurePanelNodesExpandedState: StructurePanelNodesExpandedState = {};

  revisionsData: DocumentRevisionsData = {
    revisions: [],
    currentUnsavedRevision: null
  };

  canvasKey = 1;

  get activeElementProperties() {
    if (!this.activeElementId) return null;

    return this.panelState.elementPropertiesPanel[this.activeElementId!];
  }

  get activeOxySelector() {
    if (!this.activeElementId) return null;

    return globalStore.oxySelectors.find(
      selector => selector.id === this.activeElementProperties?.oxySelector
    );
  }

  get activeOxyNestedSelector() {
    if (!this.activeElementId) return null;

    return this.activeOxySelector?.children?.find(
      selector =>
        selector.id === this.activeElementProperties?.oxyNestedSelector
    ) as OxyNestedSelector | undefined;
  }

  // BEWARE of using this action. It has huge performance penalty
  @Action
  forceUpdateEntireCanvas() {
    this.setCanvasKey(this.canvasKey + 1);
  }

  @Mutation
  setCanvasKey(newCanvasKey: number) {
    this.canvasKey = newCanvasKey;
  }

  @Mutation
  setMode(newMode: "builder" | "browse") {
    this.mode = newMode;
  }

  @Mutation
  setKeyboardTriggeredActions(
    newKeyboardTriggeredActions: KeyboardTriggeredActions
  ) {
    this.keyboardTriggeredActions = newKeyboardTriggeredActions;
  }

  @Mutation
  setOnCanvasRightClickMenuState(
    newOnCanvasRightClickMenuState: RightClickMenuState
  ) {
    this.onCanvasRightClickMenuState = newOnCanvasRightClickMenuState;
  }

  @Mutation
  setOnStructurePanelRightClickMenuState(menuState: RightClickMenuState) {
    this.onStructurePanelRightClickMenuState = menuState;
  }

  @Mutation
  setNotifications(newNotifications: BreakdanceNotification[]) {
    this.notifications = newNotifications;
  }

  @Mutation
  copyElementStudioControl(copiedControl: Control) {
    this.elementStudioClipboard = {
      ...this.elementStudioClipboard,
      copiedControl: copiedControl
    };
  }

  @Mutation
  setUnsavedChangesPresent(unsavedChangesPresent: boolean) {
    this.unsavedChangesPresent = unsavedChangesPresent;
  }

  @Mutation
  setPanelState(panelState: PanelState) {
    this.panelState = removeBannedPanelsFromPanelState(
      panelState,
      configStore.builderMode
    );
  }

  @Action
  logConsoleError(error: unknown) {
    console.log(error);
    this.setConsoleErrorsPresentFlag(true);
  }

  @Mutation
  setConsoleErrorsPresentFlag(present: boolean) {
    this.consoleErrorsPresent = present;
  }

  // @Action
  // logRecoverableError(newErrorMessage: string) {
  //   NotificationLogger.log.message(newErrorMessage);
  //   // const indexOfError = this.recoverableErrorOrNotices.findIndex(
  //   //   ({ error }) => error === newErrorMessage
  //   // );

  //   // if (indexOfError === -1) {
  //   //   this.recoverableErrorOrNotices.push({
  //   //     count: 1,
  //   //     error: newErrorMessage
  //   //   });
  //   // } else {
  //   //   this.recoverableErrorOrNotices[indexOfError].count++;
  //   // }
  // }

  @Action
  setLeftSidebarState(sidebarState: UIPanel | "closed") {
    const newPanelState = defensiveCopy(this.panelState);
    return this.setPanelState({
      ...newPanelState,
      leftSidebarPanel: sidebarState
    });
  }

  @Action
  setRightSidebarState(sidebarState: UIPanel | "closed") {
    const newPanelState = defensiveCopy(this.panelState);
    return this.setPanelState({
      ...newPanelState,
      rightSidebarPanel: sidebarState
    });
  }

  @Action
  setDesignLibraryModalState(designLibraryModalOpen: boolean) {
    const newPanelState = defensiveCopy(this.panelState);

    return this.setPanelState({
      ...newPanelState,
      designLibraryModalOpen
    });
  }

  @Action
  setCurrentElementOxySelector(id?: string) {
    if (!this.activeElementId) return;

    const newPanelState = defensiveCopy(this.panelState);

    lodash.set(
      newPanelState,
      `elementPropertiesPanel.${this.activeElementId}.oxySelector`,
      id
    );

    this.setPanelState(newPanelState);
  }

  @Action
  setCurrentElementOxyNestedSelector(id?: string) {
    if (!this.activeElementId) return;

    const newPanelState = defensiveCopy(this.panelState);

    lodash.set(
      newPanelState,
      `elementPropertiesPanel.${this.activeElementId}.oxyNestedSelector`,
      id
    );

    this.setPanelState(newPanelState);
  }

  @Action
  setCurrentElementTab(tab?: string) {
    if (!this.activeElementId) return;

    const newPanelState = defensiveCopy(this.panelState);

    lodash.set(
      newPanelState,
      `elementPropertiesPanel.${this.activeElementId}.tab`,
      tab
    );

    this.setPanelState(newPanelState);
  }

  @Mutation
  setPreferencesModalOpen(open: boolean) {
    this.panelState.preferencesModalOpen = open;
  }

  @Mutation
  setKeyboardShortcutHelpModalOpen(open: boolean) {
    this.panelState.keyboardShortcutsHelpModalOpen = open;
  }

  @Mutation
  setShouldPanelLoadFreshDataIntoStoreOnceOpened(
    panel: UIPanelWhichLoadFreshDataOnceOpened,
    should: boolean
  ) {
    this.panelState.shouldLoadFreshDataIntoStoreOnceOpened[panel] = should;
  }

  @Mutation
  toggleElementStudio() {
    this.panelState.elementStudioOpen = !this.panelState.elementStudioOpen;
  }

  @Mutation
  setInterfaceState(state: InterfaceState) {
    this.interfaceState = state;
  }

  @Mutation
  setCanvasState(state: CanvasState) {
    this.canvasState = state;
  }

  @Mutation
  logError(msg: UnrecoverableErrorMessage) {
    this.unrecoverableErrorMessages.push(msg);
  }

  @Mutation
  setActiveSelectorName(selectorName: SelectorCSSName | null) {
    this.activeSelectorName = selectorName;
  }

  @Mutation
  setActivePresetId(preset: string | null) {
    this.activePresetId = preset;
  }

  @Mutation
  setBuilderBehaviorElementId(id: null | TreeNode["id"]) {
    this.builderBehaviorElementId = id;
  }

  @Action
  activateElement(elementId: TreeNode["id"] | null) {
    const canDisableBuilderBehavior =
      this.builderBehaviorElementId &&
      elementId !== this.builderBehaviorElementId;

    if (canDisableBuilderBehavior) {
      this.setBuilderBehaviorElementId(null);
    }

    if (
      elementId === documentStore.document.tree.root.id ||
      elementId === null
    ) {
      this.activateElementMutation(null);
    } else {
      this.activateElementMutation(elementId);

      breakdanceStoreActions.run.onActivatedElement({ elementId });
      scrollToActiveElement();
    }
  }

  @Action
  setContentEditableElementId(elementId: TreeNode["id"] | null) {
    this.contentEditableElementIdMutation(elementId);
  }

  @Action
  selectNextBreakpoint() {
    const currentBreakpointIndex = configStore.breakpoints.indexOf(
      this.currentBreakpoint
    );

    if (currentBreakpointIndex === -1) return;

    const nextBreakpointIndex =
      currentBreakpointIndex + 1 < configStore.breakpoints.length
        ? currentBreakpointIndex + 1
        : 0;

    this.changeBreakpoint(configStore.breakpoints[nextBreakpointIndex]);
  }

  @Action
  changeBreakpoint(newBreakpoint: Breakpoint) {
    if (newBreakpoint.id === BASE_BREAKPOINT_ID) {
      this.selectBaseBreakpoint();
      scrollToActiveElement();
      return;
    }

    this.changeBreakpointMutation(newBreakpoint);

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width: newBreakpoint.defaultPreviewWidth
    });

    scrollToActiveElement();
  }

  @Action
  selectBaseBreakpoint() {
    const baseBreakpoint = configStore.breakpoints.find(
      breakpoint => breakpoint.id === BASE_BREAKPOINT_ID
    );

    if (!baseBreakpoint) return;

    this.changeBreakpointMutation(baseBreakpoint);

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width: "100%"
    });
  }

  @Action
  changePreviewWidthWithAdjustmentBar(previewWidthInPx: number) {
    const breakpoint = [...configStore.builtinBreakpoints]
      .reverse()
      .find(b => b.maxWidth && b.maxWidth >= previewWidthInPx);

    if (breakpoint) {
      if (breakpoint.id !== this.currentBreakpoint.id) {
        this.changeBreakpointMutation(breakpoint);
      }
    } else {
      const baseBreakpoint = configStore.builtinBreakpoints.find(
        b => b.id === BASE_BREAKPOINT_ID
      );

      if (baseBreakpoint && baseBreakpoint.id !== this.currentBreakpoint.id) {
        this.changeBreakpointMutation(baseBreakpoint);
      }
    }

    this.changePreviewWidthMutation({
      ...this.canvasPreviewWidth,
      width:
        DEFAULT_MINIMUM_BREAKPOINT_WIDTH > previewWidthInPx
          ? DEFAULT_MINIMUM_BREAKPOINT_WIDTH
          : previewWidthInPx
    });
  }

  @Mutation
  setCanvasDragPlaceholderPosition(
    canvasDragPlaceholderPosition: CanvasDragPlaceholderPosition | null
  ) {
    this.canvasDragPlaceholderPosition = canvasDragPlaceholderPosition;
  }

  dragMousePosition: { x: number; y: number } | null = null;

  @Mutation
  setDragMousePosition(dragMousePosition: { x: number; y: number } | null) {
    this.dragMousePosition = dragMousePosition;
  }

  @Mutation
  activateElementMutation(elementId: TreeNode["id"] | null) {
    this.activeElementId = elementId;
  }

  @Mutation
  contentEditableElementIdMutation(elementId: TreeNode["id"] | null) {
    this.elementWithContentEditable = elementId;
  }

  @Mutation
  setLastHoveredOnCanvasElementId(elementId: TreeNode["id"] | null) {
    this.lastHoveredOnCanvasElementId = elementId;
  }

  @Mutation
  changeBreakpointMutation(newBreakpoint: Breakpoint) {
    this.currentBreakpoint = newBreakpoint;
  }

  @Mutation
  changePreviewWidthMutation(previewWidth: CanvasPreviewWidth) {
    this.canvasPreviewWidth = previewWidth;
  }

  @Mutation
  setAddElementDraggedSlug(draggedElementSlug: string | null) {
    this.currentlyDraggingAddElementSlug = draggedElementSlug;
  }

  @Mutation
  setCanvasElementDraggedId(draggedElementId: TreeNode["id"] | null) {
    this.currentlyDraggingCanvasElementId = draggedElementId;
  }

  @Mutation
  endCanvasElementDrag() {
    this.currentlyDraggingCanvasElementId = null;
  }

  @Mutation
  setDragMixinDragging(isDragMixinDragging: boolean) {
    this.isDragMixinDragging = isDragMixinDragging;
  }

  @Mutation
  setStructurePanelNodesExpandedState(
    newStructurePanelNodesExpandedState: StructurePanelNodesExpandedState
  ) {
    this.structurePanelNodesExpandedState = defensiveCopy(
      newStructurePanelNodesExpandedState
    );
  }

  get activeElement(): false | TreeNode {
    if (this.activeElementId === null) return false;

    const foundNode = findNode(
      documentStore.document.tree,
      this.activeElementId
    );
    return foundNode || false;
  }

  get builderBehaviorActiveElement(): false | TreeNode {
    if (this.builderBehaviorElementId === null) return false;

    const foundNode = findNode(
      documentStore.document.tree,
      this.builderBehaviorElementId
    );

    return foundNode || false;
  }

  get activeSelector() {
    if (!this.activeSelectorName) return false;

    const foundSelector = globalStore.cssSelectors.find(
      selector => selector.name === this.activeSelectorName
    );

    return foundSelector ? foundSelector : false;
  }

  get activePreset() {
    if (!this.activePresetId) return null;

    const foundPreset = globalStore.presets.find(
      preset => preset.id === this.activePresetId
    );

    return foundPreset ? foundPreset : null;
  }

  get isCanvasElementBeingDragged() {
    return this.currentlyDraggingCanvasElementId !== null;
  }

  get isAddElementBeingDragged() {
    return this.currentlyDraggingAddElementSlug !== null;
  }

  get hasFullAccess() {
    return configStore.permission?.slug === "full";
  }

  get inlineEditingElement() {
    if (!this.inlineEditingContext) return null;

    const foundNode = findNode(
      documentStore.document.tree,
      this.inlineEditingContext.elementId
    );

    return foundNode || null;
  }

  get inlineEditingElementPropertyOverrides() {
    if (!this.inlineEditingElement) return [];
    return getValuesFromComponent(this.inlineEditingElement);
  }

  @Mutation
  setLoggedOut(isLoggedOut: boolean) {
    this.isLoggedOut = isLoggedOut;
  }

  @Mutation
  setElementUpdatingState(newElementUpdateState: {
    elementId: TreeNode["id"];
    isUpdating: boolean;
  }) {
    Vue.set(
      this.elementsThatAreUpdating,
      newElementUpdateState.elementId,
      newElementUpdateState.isUpdating
    );
  }

  @Mutation
  setIdsOfPostsToRegenerateCache(maybePostIds: TreeNode["id"][] | null) {
    this.idsOfPostsToRegenerateCache = maybePostIds;
  }

  @Mutation
  requestClosingConfirmation({
    subject,
    confirmationDefinition
  }: {
    subject: ClosingConfirmationSubject;
    confirmationDefinition: ClosingConfirmationDefinition;
  }) {
    const confirmationItem: ClosingConfirmationItem = {
      ...confirmationDefinition,
      show: false,
      isSaving: false,
      isDiscarding: false
    };
    Vue.set(this.closingConfirmations, subject, confirmationItem);
  }

  @Mutation
  chooseOptionForClosingConfirmation({
    subject,
    chosenOption
  }: {
    subject: ClosingConfirmationSubject;
    chosenOption: ClosingConfirmationChosenOption;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.chosenOption`,
        chosenOption
      );
    }
  }

  @Mutation
  showClosingConfirmation(subject: ClosingConfirmationSubject) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(this.closingConfirmations, `${subject}.show`, true);
    }
  }

  @Mutation
  hideClosingConfirmation(subject: ClosingConfirmationSubject) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(this.closingConfirmations, `${subject}.show`, false);
    }
  }

  @Mutation
  noClosingConfirmationIsNeededFor(subject: ClosingConfirmationSubject) {
    VueSimpleDeepset(this.closingConfirmations, subject, undefined);
  }

  @Mutation
  setClosingConfirmationIsDiscarding({
    subject,
    isDiscarding
  }: {
    subject: ClosingConfirmationSubject;
    isDiscarding: boolean;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.isDiscarding`,
        isDiscarding
      );
    }
  }

  @Mutation
  setClosingConfirmationIsSaving({
    subject,
    isSaving
  }: {
    subject: ClosingConfirmationSubject;
    isSaving: boolean;
  }) {
    if (this.closingConfirmations[subject]) {
      VueSimpleDeepset(
        this.closingConfirmations,
        `${subject}.isSaving`,
        isSaving
      );
    }
  }

  @Mutation
  setLastModifiedElementProperty({
    elementId,
    path
  }: LastModifiedElementProperty) {
    this.lastModifiedElementProperty = {
      elementId,
      path
    };
  }

  @Mutation
  setRefreshSsrElementId(elementId: TreeNode["id"] | null) {
    this.refreshSsrElementId = elementId;
  }

  @Mutation
  setSettings(newSettings: UISettings) {
    this.settings = newSettings;
  }

  @Mutation
  setRevisionsData(newRevisionsData: DocumentRevisionsData) {
    this.revisionsData = newRevisionsData;
  }

  @Mutation
  togglePreviewMode() {
    this.isPreviewMode = !this.isPreviewMode;
  }

  @Mutation
  closePreviewMode() {
    this.isPreviewMode = false;
  }

  @Mutation
  setInlineEditingContext(inlineEditingContext: InlineEditingContext | null) {
    this.inlineEditingContext = inlineEditingContext;
  }

  @Mutation
  resetInlineEditingContext() {
    const id = this.inlineEditingContext?.elementId;

    breakdanceUndo.transaction(
      () => {
        this.inlineEditingContext = null;
      },
      "Stop Inline Editing",
      `inline-editing:stop:${id}`
    );
  }
}
